<script setup>
import { computed, onMounted } from 'vue'
import { Icon, ProfilePicture } from '@components'

import { useAppointments } from '@store/appointments.js'
const $appointments = useAppointments()

import { useSettings } from '@store/settings.js'
const $settings = useSettings()

import { useCustomizer } from '@store/customizer.js'
const $customizer = useCustomizer()

import moment from 'moment'

import { getPrice, addSpaceAfterComma, showDurationUnit } from '@utils'

const customer = computed(() => {
    return prebook.customer || {}
})
const business = prebook.business || {}

const calculateTotal = () => {
    return $appointments.selectedService?.price || 0
}

const businessAddress = computed(() => {
    let addressParts = [
        business.address.street,
        business.address.state,
        business.address.city,
        business.address.zip_code,
        business.address.country_name
    ]

    // Filter out empty or undefined parts
    addressParts = addressParts.filter(part => part?.trim?.() !== '')

    console.log(addressParts)

    // Return the formatted address or "N/A" if empty
    return addressParts.length ? addressParts.join(', ') : ""
})


// format time depending on customizer
const formatTime = (time, format) => {
    if ($settings.customizer.time.show_time) {
        return moment(time, format).format('HH:mm')
    } else {
        return moment(time, format).format('hh:mm A')
    }
}

const PAYMENT = prebook.settings.payment
const availablePaymentMethods = computed(() => {
    return PAYMENT.methods.filter(method => method.enabled)
})

</script>

<template>
    <div class="overview-information-wrapper">
        <!-- service info -->
        <div class="service-info-wrapper" v-if="$appointments.selectedService">
            <div class="service-info-subtitle capitalize">{{ $settings.labels?.service?.singular }}</div>
            <div class="service-info-wrapper-2">
                <div class="service-info-title" :style="$customizer.subtitleStyle">{{ $appointments.selectedService?.name }}</div>
                <div class="badge-wrapper">
                    <Icon name="clock" size="12px" class="text-[#667085]" />
                    <div class="badge-title">{{ showDurationUnit($appointments.selectedService?.duration, $appointments.selectedService?.duration_unit, false) }}</div>
                </div>
            </div>
        </div>

        <!-- divider -->
        <div class="divider"></div>

        <div class="user-info-wrapper" v-if="$settings.customizer.booking_overview.show_staff || $settings.customizer.booking_overview.show_customer">
            <!-- agent info -->
            <div class="user-wrapper" v-if="$settings.customizer.booking_overview.show_staff && $settings.addons.staffs">
                <ProfilePicture :name="$appointments.selectedStaff.name" :img="$appointments.selectedStaff.profile_image_url" :bgColor="$appointments.selectedStaff.color" textClass="user-image" imgClass="user-image" />

                <div class="user-content">
                    <div class="user-type capitalize">{{ $settings.labels?.staff?.singular }}</div>
                    <div class="user-name" :style="$customizer.subtitleStyle">{{ $appointments.selectedStaff?.name }}</div>
                </div>
            </div>
            <!-- customer info -->
            <div class="user-wrapper" v-if="$settings.customizer.booking_overview.show_customer">
                <ProfilePicture :name="customer.display_name || customer.name || customer.first_name || customer.last_name" :img="customer.picture" textClass="user-image" imgClass="user-image" />

                <div class="user-content">
                    <div class="user-type capitalize">{{ $settings.labels?.customer?.singular }}</div>
                    <div class="user-name" :style="$customizer.subtitleStyle">{{ customer.display_name || customer.name || customer.first_name || customer.last_name }}</div>
                </div>
            </div>
        </div>

        <div class="info-wrapper" :class="{ '!rounded-none': !$customizer.isRounded }">
            <!-- date -->
            <div class="info-item">
                <Icon name="calendar" size="24px" class="text-[#667085]" />
                <div class="info-text">
                    <div class="font-semibold">{{ moment($appointments.appointment.booking_date?.fullDate, 'YYYY-MM-DD').format('MMMM DD, YYYY') }}</div>
                    <div class="font-medium">{{ formatTime($appointments.appointment.booking_time_start, 'HH:mm') }} - {{ formatTime($appointments.appointment.booking_time_end, 'HH:mm') }}</div>
                </div>
            </div>

            <!-- divider -->
            <div class="divider" v-if="businessAddress && $settings.customizer.booking_overview.show_address"></div>

            <!-- location -->
            <div class="info-item" v-if="businessAddress && $settings.customizer.booking_overview.show_address">
                <Icon name="markerPin01" size="24px" class="text-[#667085]" />
                <div class="font-medium info-text">{{ addSpaceAfterComma(businessAddress) }}</div>
            </div>
        </div>

        <div v-if="$settings.customizer.booking_overview.show_payment_summary && $appointments.selectedService.price_type != 'none'" class="cost-wrapper">
            <!-- service amount -->
            <div class="cost-item-1">
                <div>{{ $appointments.selectedService?.name }}</div>
                <div>{{ $appointments.selectedService?.price_type == 'free' ? "Free" : getPrice($appointments.selectedService?.price) }}</div>
            </div>

            <!-- extra amount -->
            <!-- <div class="mt-2 cost-item-1">
                <div>Extras</div>
                <div>$10</div>
            </div> -->

            <!-- subtotal amount -->
            <!-- <div class="mt-3 cost-item-2">
                <div>Sub total</div>
                <div>$110</div>
            </div> -->

            <!-- tax amount -->
            <!-- <div class="mt-2 cost-item-1">
                <div>Salex tax (8%)</div>
                <div>$8.80</div>
            </div> -->

            <!-- divider -->
            <div v-if="$settings.customizer.booking_overview.show_payment_summary" class="my-3 divider"></div>

            <!-- total amount -->
            <div v-if="$settings.customizer.booking_overview.show_payment_summary" class="cost-item-2">
                <div>Total</div>
                <div>{{ $appointments.selectedService?.price_type == 'free' ? "Free" : getPrice($appointments.selectedService?.price) }}</div>
            </div>

            <!-- divider -->
            <div class="my-3 divider" v-if="$settings.customizer.booking_overview.show_payment_method && $settings.addons.payment && $appointments.selectedService?.price_type == 'free'"></div>

            <!-- Payment methods  -->
            <div class="flex flex-col gap-4" v-if="$settings.customizer.booking_overview.show_payment_method && $settings.addons.payment && $appointments.selectedService?.price_type == 'fixed'">
                <div class="capitalize text-base">{{ $settings.labels?.payment?.singular }} method</div>
                <!-- Radio  -->
                <div class="flex flex-col gap-2" v-for="method in availablePaymentMethods">
                    <div class="flex gap-2">
                        <input type="radio" :id="method.id" name="prebook-payment-method" :value="method.id" v-model="$appointments.appointment.payment_method" />
                        <label :for="method.id" class="text-base">{{ method.label }}</label>
                    </div>
                    <div class="bg-gray-50 border border-gray-100 px-5 py-3 rounded text-sm" v-if="$appointments.appointment.payment_method == method.id">
                        {{ method.description }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<style lang="scss" scoped></style>
